<template>
  <WdsSelect
    :model-value="displayValue"
    can-deselect
    multi-select
    :list="statusesOptions"
    :placeholder="$t('Status')"
    class="tw-min-w-[150px]"
    data-test="tc-filter-status"
    :max-labels-visible="meta?.filter?.quickFilter ? 1 : 5"
    labels-primary
    @update:model-value="onDisplayChange"
  >
    <template #option="{ option }">
      <div :data-test="`filter-status-${option.label.replace(' ', '-').toLowerCase()}`">
        {{ option.label }}
      </div>
    </template>
  </WdsSelect>
</template>

<script setup lang="ts">
import { WdsSelect } from '@worksome/wds-components'
import { computed, inject, ModelRef, onMounted, ref, watch } from 'vue'
import { $t } from '@/composables/translation'
import { DefaultStatuses } from '@/components/GraphqlDataTable/filters/trustedContacts/injection'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'

const { meta } = defineProps<{ meta: FilterMetaDataProp }>()

const selectedStatuses: ModelRef<{ label: string; value: string }[]> = defineModel({
  default: () => [],
})

const statusesOptions = [
  { label: $t('Active'), value: 'ACTIVE' },
  { label: $t('Pending invite'), value: 'INVITED' },
  { label: $t('Application declined'), value: 'BLOCKED' },
]

const defaultStatuses = inject(DefaultStatuses, ref([]))

const statusValueToOption = (value: string) =>
  statusesOptions.find((opt) => opt.value === value)

const defaultOptions = computed(() =>
  defaultStatuses.value
    .map(statusValueToOption)
    .filter((opt): opt is { label: string; value: string } => opt !== undefined),
)

const displayValue = computed(() => {
  const raw = selectedStatuses.value
  if (!Array.isArray(raw)) return []
  return raw
    .map((item: unknown) => {
      if (item && typeof item === 'object' && 'value' in (item as object)) {
        return item as { label: string; value: string }
      }
      return statusValueToOption(String(item))
    })
    .filter((opt): opt is { label: string; value: string } => opt !== undefined)
})

function onDisplayChange(val: { label: string; value: string }[]) {
  selectedStatuses.value = val
}

onMounted(() => {
  if (selectedStatuses.value.length === 0 && defaultStatuses.value.length > 0) {
    selectedStatuses.value = [...defaultOptions.value]
  }
})

watch(defaultStatuses, (newDefaults) => {
  const opts = newDefaults
    .map(statusValueToOption)
    .filter((opt): opt is { label: string; value: string } => opt !== undefined)
  selectedStatuses.value = opts
})
</script>
